import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, inr } from "@/lib/api";
import { copySymbols } from "@/pages/Scanner";
import { SectorPulse } from "@/components/SectorPulse";
import { OIPanel } from "@/components/OIPanel";
import { PocCrossPanel } from "@/components/PocCrossPanel";
import { TfrAdrPanel } from "@/components/TfrAdrPanel";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { useFlow, FlowTags, FlowNote } from "@/components/FlowMarks";
import { DayReports } from "@/components/DayReports";
import { ArrangeGrid, ResetLayout, useTodayLayout } from "@/components/ArrangeGrid";
import {
  BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip as RTooltip,
} from "recharts";
import {
  LayoutDashboard, Radio, Target, Radar, Activity, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Newspaper, ExternalLink, Clock, BarChart2, Layers, Sparkles, Copy, Gem,
} from "lucide-react";
import { PlatinumPanel } from "@/components/PlatinumPanel";

const fmtIST = (ts) => (ts ? new Date(ts).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : "");
const isMarketOpen = () => {
  const n = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  if (n.getDay() === 0 || n.getDay() === 6) return false;
  const m = n.getHours() * 60 + n.getMinutes();
  return m >= 555 && m <= 930;
};

const ZoneTags = ({ tags, testid }) => (tags?.length ? (
  <span className="inline-flex flex-wrap gap-0.5" data-testid={testid}>
    {tags.map((t) => (
      <span key={t} title={`${{ D: "Day", W: "Week", M: "Month" }[t[0]]} Flow Zone ${t.endsWith("Coil") ? "coil (inside previous zone)" : "compression (narrow)"}`}
        className={`text-[8px] font-mono font-bold px-1 py-px rounded border ${t.endsWith("Coil") ? "bg-violet-100 text-violet-800 border-violet-300/70" : "bg-amber-100 text-amber-900 border-amber-300/70"}`}>{t}</span>
    ))}
  </span>
) : null);

const Chg = ({ v }) => (
  <span className={`font-mono text-[12px] ${v == null ? "text-slate-400" : v >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
    {v == null ? "—" : `${v >= 0 ? "+" : ""}${v}%`}
  </span>
);

function Panel({ title, icon: Icon, hint, children, testid, accent = "blue", className = "" }) {
  const ring = { blue: "text-blue-600", amber: "text-amber-600", emerald: "text-emerald-600", rose: "text-rose-600", cyan: "text-cyan-600", violet: "text-violet-600" }[accent];
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className={`rounded-2xl border border-slate-200/90 bg-white card-soft neon-edge overflow-hidden p-3.5 ${className}`} data-testid={testid}>
      <div className="flex items-start gap-2 mb-2.5">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${ring}`} />
        <div className="min-w-0">
          <div className="font-display font-bold text-slate-900 text-[13px] leading-tight whitespace-nowrap">{title}</div>
          {hint && <div className="text-[9.5px] font-mono text-slate-400 leading-snug mt-0.5">{hint}</div>}
        </div>
      </div>
      {children}
    </motion.div>
  );
}

function StockRow({ r, navigate, testid, showStar, showGap, showRvol, showTime, showOpen, hot, marks }) {
  const isTop = hot?.top?.includes(r.sector);
  const isBottom = hot?.bottom?.includes(r.sector);
  const hasMeta = (showTime && r.since) || showRvol || showGap || showOpen || isTop || isBottom;
  return (
    <div className="flex items-start gap-1">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
    <button data-testid={testid}
      className={`flex-1 min-w-0 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-slate-100 border border-transparent hover:border-slate-200 text-left ${isTop ? "bg-emerald-50/70" : isBottom ? "bg-rose-50/70" : ""}`}>
      {/* line 1: symbol + price (price wraps below when the column is narrow) */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
        <span className="flex items-center gap-1.5 min-w-0">
          {showStar && r.star ? <span className={`h-2 w-2 rounded-full shrink-0 ${r.star === 3 ? "bg-emerald-500" : "bg-amber-400"}`} title={`${r.star}★ Big Money Volume`} /> : null}
          <span className="font-display font-bold text-slate-900 text-[13px] whitespace-nowrap" title={`${r.name} (${r.symbol})`}>{r.symbol}</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-[13px] text-slate-900">₹{inr(r.ltp)}</span>
          <Chg v={r.change_pct} />
        </span>
      </div>
      {/* line 2: meta — time · sector · highlights */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 min-h-[14px]">
        {showTime && r.since && (
          <span className="flex items-center gap-0.5 text-[9px] font-mono text-sky-700 bg-sky-100/80 px-1 py-px rounded shrink-0" data-testid={`${testid}-time`} title="First seen in this scanner today">
            <Clock className="h-2.5 w-2.5" />{r.since}
          </span>
        )}
        <span className="text-[9px] font-mono text-slate-400 whitespace-nowrap">{r.sector}</span>
        <ZoneTags tags={r.zone_tags} testid={`${testid}-zonetags`} />
        {marks && <FlowTags m={marks[r.symbol]} testid={testid} />}
        {isTop && <span className="text-[8px] font-mono font-bold px-1 py-px rounded bg-emerald-600 text-white shrink-0" data-testid={`${testid}-topsector`} title="Top-2 performing sector">TOP</span>}
        {isBottom && <span className="text-[8px] font-mono font-bold px-1 py-px rounded bg-rose-600 text-white shrink-0" data-testid={`${testid}-bottomsector`} title="Bottom-2 performing sector">WORST</span>}
        {showRvol && <span className="text-[9px] font-mono text-amber-700 shrink-0">{r.rvol5 != null ? `5m ${r.rvol5}×` : ""}{r.rvol15 != null ? ` · 15m ${r.rvol15}×` : ""}</span>}
        {showOpen && r.open_x != null && (
          <span data-testid={`${testid}-openx`} title={`9:15 candle volume ${r.open_vol?.toLocaleString("en-IN")} vs ${r.open_days}-day avg ${r.open_avg?.toLocaleString("en-IN")}`}
            className={`text-[9px] font-mono font-bold px-1 py-px rounded shrink-0 ${r.open_tier >= 5 ? "bg-amber-500 text-white" : r.open_tier >= 3 ? "text-amber-700 bg-amber-100 border border-amber-300" : "text-amber-700"}`}>
            open vol {r.open_x}× {r.open_dir === "up" ? "▲" : r.open_dir === "down" ? "▼" : ""}
          </span>
        )}
        {showGap && r.gap_pct != null && <span className={`text-[9px] font-mono font-bold shrink-0 ${r.gap_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>gap {r.gap_pct >= 0 ? "+" : ""}{r.gap_pct}%</span>}
        {!hasMeta && <span />}
      </div>
    </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} className="mt-2" />
    </div>
  );
}

const SORTS = {
  default: { label: "Default", fn: null },
  chg: { label: "Change %", fn: (a, b) => Math.abs(b.change_pct || 0) - Math.abs(a.change_pct || 0) },
  time: { label: "Entry time", fn: (a, b) => (a.since || "99").localeCompare(b.since || "99") },
  vol: { label: "Open volume", fn: (a, b) => (b.open_x || 0) - (a.open_x || 0) },
  sector: { label: "Sector", fn: (a, b) => (a.sector || "").localeCompare(b.sector || "") || a.symbol.localeCompare(b.symbol) },
};

function MiniList({ rows: raw, navigate, testid, limit = 6, ...flags }) {
  const [all, setAll] = useState(false);
  const [sort, setSort] = useState("default");
  const rows = SORTS[sort].fn ? [...raw].sort(SORTS[sort].fn) : raw;
  return (
    <div data-testid={testid}>
      {raw.length > 0 && (
        <div className="flex items-center justify-end gap-1.5 mb-1">
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid={`${testid}-sort`} title="Sort list"
            className="h-5 rounded border border-slate-200 bg-white px-1 text-[9px] font-mono text-slate-600">
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => copySymbols(rows.map((r) => r.symbol), testid)} data-testid={`${testid}-copy`} title="Copy symbols (comma-separated)"
            className="h-5 w-5 grid place-items-center rounded border border-slate-200 text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-colors">
            <Copy className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className={`space-y-1 ${all ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
        {(all ? rows : rows.slice(0, limit)).map((r) => <StockRow key={r.instrument_id} r={r} navigate={navigate} testid={`${testid}-${r.symbol}`} {...flags} />)}
      </div>
      {rows.length === 0 && <p className="text-slate-400 text-[11px] py-3 text-center font-mono">none right now</p>}
      {rows.length > limit && (
        <button onClick={() => setAll((v) => !v)} data-testid={`${testid}-show-all`}
          className="w-full text-[10px] font-mono text-blue-700 hover:text-blue-900 text-center pt-1.5">
          {all ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

function OpeningSurge({ rows, navigate, hot }) {
  const [min, setMin] = useState(Number(localStorage.getItem("tfr_open_min") || 2));
  const pick = (v) => { localStorage.setItem("tfr_open_min", String(v)); setMin(v); };
  return (
    <div data-testid="opening-surge">
      <div className="flex items-center gap-1 mb-1.5" data-testid="opening-filter">
        {[2, 3, 5].map((v) => (
          <button key={v} onClick={() => pick(v)} data-testid={`opening-filter-${v}`}
            className={`px-2 h-5 rounded text-[10px] font-mono font-bold border ${min === v ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 text-slate-500 hover:text-amber-600"}`}>{v}×+</button>
        ))}
      </div>
      <MiniList rows={rows.filter((r) => r.open_x >= min)} navigate={navigate} testid="opening" limit={10} showOpen hot={hot} />
    </div>
  );
}

function BullBear({ data, navigate, testid, limit = 5, ...flags }) {
  return (
    <div className="space-y-3">
      {[["bull", "Bullish", "text-emerald-600", TrendingUp], ["bear", "Bearish", "text-rose-600", TrendingDown]].map(([k, lbl, cls, Ic]) => (
        <div key={k}>
          <div className={`flex items-center gap-1 text-[11px] font-semibold mb-1.5 ${cls}`}>
            <Ic className="h-3.5 w-3.5" /> {lbl} <span className="text-slate-400 font-mono">({data[k].length})</span>
          </div>
          <MiniList rows={data[k]} navigate={navigate} testid={`${testid}-${k}`} limit={limit} {...flags} />
        </div>
      ))}
    </div>
  );
}

const CoilBadge = ({ tfs, testid }) => (tfs?.length >= 2 ? (
  <span data-testid={testid} title={`Coiling on ${tfs.join(" + ")}`}
    className={`text-[8px] font-mono font-bold px-1 py-px rounded shrink-0 ${tfs.length === 3 ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-white shadow-[0_0_8px_rgba(245,158,11,0.45)]" : "bg-amber-100 text-amber-800 border border-amber-300/70"}`}>
    {tfs.length === 3 ? "TRIPLE COIL" : `COIL ${tfs.join("+")}`}
  </span>
) : null);

function MultiCoilStrip({ mc, navigate }) {
  const triple = mc?.triple || [];
  const double = mc?.double || [];
  const chip = (r, tier) => (
    <button key={r.instrument_id} onClick={() => navigate(`/app/instrument/${r.instrument_id}`)} data-testid={`multicoil-${tier}-${r.symbol}`}
      title={`${r.name} · coiling on ${r.coil_tfs.join(" + ")}`}
      className={`text-[11px] font-display font-bold px-2 py-0.5 rounded-md transition-colors ${tier === "triple" ? "bg-white text-amber-800 border border-amber-400 hover:bg-amber-50" : "bg-white/70 text-slate-800 border border-amber-200 hover:bg-white"}`}>
      {r.symbol} {tier === "double" && <span className="font-mono text-[9px] text-amber-700">{r.coil_tfs.join("+")}</span>}
    </button>
  );
  return (
    <div className="rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 p-2.5 mb-2.5 shadow-[0_0_14px_rgba(245,158,11,0.18)]" data-testid="multicoil-strip">
      <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800 mb-1.5">
        <Sparkles className="h-3.5 w-3.5 text-amber-500" /> TRIPLE COIL
        <span className="font-mono font-normal text-[9px] text-amber-700/80">Day + Week + Month coiling · rare high-conviction</span>
      </div>
      {triple.length ? (
        <div className="flex flex-wrap gap-1.5" data-testid="multicoil-triple">{triple.map((r) => chip(r, "triple"))}</div>
      ) : (
        <p className="text-[10px] font-mono text-amber-700/80" data-testid="multicoil-none">No triple coil today</p>
      )}
      {double.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] font-mono font-bold text-amber-700 mb-1">DOUBLE COIL · 2 of 3 ({double.length})</div>
          <div className="flex flex-wrap gap-1.5" data-testid="multicoil-double">{double.map((r) => chip(r, "double"))}</div>
        </div>
      )}
    </div>
  );
}

function ZonePanel({ zones, narrowPct, multiCoil, weekBasis, navigate, flow }) {
  const [per, setPer] = useState("D");
  const [tab, setTab] = useState("compression");
  const [all, setAll] = useState(false);
  const zr = zones?.[per] || {};
  const z = { compression: flow.filter(zr.compression), coil: flow.filter(zr.coil) };
  const mc = { triple: flow.filter(multiCoil?.triple), double: flow.filter(multiCoil?.double) };
  const rows = z[tab] || [];
  const pername = { D: "Day", W: "Week", M: "Month" }[per];
  const lim = all ? rows.length : 6;
  const tabs = [["compression", "Compression"], ["coil", "Coil"]];
  return (
    <Panel title="Flow Zone Setups" icon={Layers} accent="violet" testid="panel-zones" hint="Day = intraday · Week = swing · Month = positional">
      <MultiCoilStrip mc={mc} navigate={navigate} />
      <FlowNote />
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 mb-2" data-testid="zones-period">
        {[["D", "Day"], ["W", "Week"], ["M", "Month"]].map(([k, lbl]) => (
          <button key={k} onClick={() => { setPer(k); setAll(false); }} data-testid={`zones-period-${k}`}
            className={`text-[11px] font-bold px-3 py-0.5 rounded-md transition-colors ${per === k ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {lbl}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mb-2" data-testid="zones-tabs">
        {tabs.map(([k, lbl]) => (
          <button key={k} onClick={() => { setTab(k); setAll(false); }} data-testid={`zones-tab-${k}`}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${tab === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
            {lbl} <span className="font-mono opacity-80">({z[k]?.length || 0})</span>
          </button>
        ))}
      </div>
      <p className="text-[9px] font-mono text-slate-400 mb-1.5" data-testid="zones-legend">
        {tab === "compression" ? `${pername} Flow Zone${per === "W" ? (weekBasis === "next" ? " (next week)" : " (this week)") : ""} width ≤ ${narrowPct?.[per]}% of price — expansion likely` : `${pername} Flow Zone${per === "W" && weekBasis === "next" ? " (next week)" : ""} nested inside ${{ D: "yesterday's", W: weekBasis === "next" ? "this week's" : "last week's", M: "last month's" }[per]} zone — coiling`} · <span className="text-amber-600">★ = Compression + Coil</span>
      </p>
      <div className={`space-y-1 ${all ? "max-h-[420px] overflow-y-auto pr-1" : ""}`} data-testid={`zones-list-${per}-${tab}`}>
        {rows.slice(0, lim).map((r) => (
          <button key={r.instrument_id} data-testid={`zones-${per}-${tab}-${r.symbol}`} onClick={() => navigate(`/app/instrument/${r.instrument_id}`)}
            className={`w-full rounded-lg px-2.5 py-1.5 transition-colors hover:bg-slate-100 border border-transparent hover:border-slate-200 text-left ${r.both ? "bg-amber-50/70" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
              <span className="flex items-center gap-1.5 min-w-0">
                {r.both && <span className="text-amber-500 text-[12px] leading-none" title="Compression + Coil" data-testid={`zones-${per}-${tab}-${r.symbol}-star`}>★</span>}
                <span className="font-display font-bold text-slate-900 text-[13px] whitespace-nowrap" title={`${r.name} (${r.symbol})`}>{r.symbol}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-[13px] text-slate-900">₹{inr(r.ltp)}</span>
                <Chg v={r.change_pct} />
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
              <span className="text-[9px] font-mono text-violet-700 bg-violet-100/80 px-1 py-px rounded shrink-0">width {r.width}%</span>
              <span className="text-[9px] font-mono text-slate-400 truncate">{r.sector}</span>
              <FlowTags m={flow.marks[r.symbol]} testid={`zones-${per}-${tab}-${r.symbol}`} />
              <CoilBadge tfs={r.coil_tfs} testid={`zones-${per}-${tab}-${r.symbol}-coilbadge`} />
            </div>
          </button>
        ))}
        {rows.length === 0 && <p className="text-slate-400 text-[11px] py-3 text-center font-mono">none today</p>}
        {rows.length > 6 && (
          <button onClick={() => setAll((v) => !v)} data-testid="zones-show-all"
            className="w-full text-[10px] font-mono text-violet-700 hover:text-violet-900 text-center pt-1">
            {all ? "Show less" : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    </Panel>
  );
}

function SectorBars({ sectors }) {
  const data = sectors.map((s) => ({ name: s.sector, adv: s.adv, dec: -s.dec, perf: s.perf }));
  return (
    <div style={{ width: "100%", height: Math.max(400, sectors.length * 27) }} data-testid="sector-ad-chart">
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" stackOffset="sign" margin={{ left: 4, right: 12, top: 2, bottom: 2 }} barCategoryGap={3}>
          <XAxis type="number" tick={{ fontSize: 9, fontFamily: "monospace", fill: "#94a3b8" }} />
          <YAxis type="category" dataKey="name" width={124}
            tick={{ fontSize: 9, fontFamily: "monospace", fill: "#475569" }}
            tickFormatter={(v) => { const s = sectors.find((x) => x.sector === v); return s ? `${v}  ${s.perf >= 0 ? "+" : ""}${s.perf}%` : v; }} />
          <RTooltip
            contentStyle={{ fontSize: 11, fontFamily: "monospace", borderRadius: 2, border: "1px solid #e2e8f0", background: "#ffffff", color: "#0f172a" }} itemStyle={{ color: "#0f172a" }} labelStyle={{ color: "#2563eb" }} cursor={{ fill: "rgba(59,130,246,0.08)" }}
            formatter={(v, n) => [Math.abs(v), n === "adv" ? "Advances" : "Declines"]}
            labelFormatter={(l) => { const s = sectors.find((x) => x.sector === l); return `${l} · ${s?.index_name || "constituent avg"} ${s ? `${s.perf >= 0 ? "+" : ""}${s.perf}%` : ""}`; }} />
          <Bar dataKey="adv" stackId="s" radius={[2, 2, 2, 2]}>{data.map((d, i) => <Cell key={i} fill="#10b981" />)}</Bar>
          <Bar dataKey="dec" stackId="s" radius={[2, 2, 2, 2]}>{data.map((d, i) => <Cell key={i} fill="#f43f5e" />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function NewsPanel({ news }) {
  if (!news) return <p className="text-slate-400 text-[11px] py-3 font-mono">Loading…</p>;
  if (!news.configured) return <p className="text-slate-400 text-[11px] py-3 font-mono" data-testid="news-not-configured">News feed not configured.</p>;
  if (!news.items?.length) return <p className="text-slate-400 text-[11px] py-3 font-mono">No market news right now.</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="news-list">
      {news.items.map((n) => (
        <a key={n.uuid || n.url} href={n.url} target="_blank" rel="noreferrer" data-testid="news-item"
          className="block rounded-lg px-3 py-2 transition-colors hover:bg-slate-100 border border-slate-100 hover:border-slate-200">
          <div className="flex items-start justify-between gap-2">
            <span className="text-[12px] font-semibold text-slate-800 leading-snug">{n.title}</span>
            <ExternalLink className="h-3 w-3 text-slate-400 shrink-0 mt-0.5" />
          </div>
          <div className="flex items-center gap-2 mt-1 text-[9px] font-mono text-slate-400">
            <span>{n.source}</span>
            {n.symbols?.slice(0, 3).map((s) => <span key={s} className="text-cyan-700 bg-cyan-100/70 px-1 rounded">{s}</span>)}
          </div>
        </a>
      ))}
    </div>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const flow = useFlow();
  const layout = useTodayLayout();
  const [d, setD] = useState(null);
  const [news, setNews] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    api.get("/today/dashboard").then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/today/news").then((r) => setNews(r.data)).catch(() => setNews({ configured: false }));
    setUpdatedAt(new Date());
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (isMarketOpen()) load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-3" data-testid="today-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-blue-600" /> Trading Dashboard
          </h1>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">Scanners show only Flow Zone stocks (Compression / Coil · D/W/M) · sector chart = full market, best → worst</p>
        </div>
        <span className="flex items-center gap-2">
        <ResetLayout lay={layout.lay} save={layout.save} />
        <DayReports />
        <span className={`flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full border ${isMarketOpen() ? "text-emerald-700 border-emerald-400/60 bg-emerald-100/70" : "text-slate-500 border-slate-300 bg-slate-100"}`} data-testid="market-status">
          <Radio className={`h-3 w-3 ${isMarketOpen() ? "text-emerald-600" : "text-slate-500"}`} /> {isMarketOpen() ? "MARKET OPEN" : "MARKET CLOSED"}
          {updatedAt && <span className="text-slate-500">· {fmtIST(updatedAt)} IST</span>}
        </span>
        </span>
      </div>

      {!d ? (
        <div className="py-20 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div>
      ) : (
        <>
          <Panel title="TFR Platinum" icon={Gem} accent="blue" testid="panel-platinum" hint="in 2+ of TFR Prime · TFR Gold · Power Scan, same direction">
            <PlatinumPanel limit={6} />
          </Panel>
          <SectorPulse sectors={d.sectors} />
          {/* masonry: panels stack in columns so there are no empty gaps; members can pin / drag boxes */}
          <ArrangeGrid lay={layout.lay} save={layout.save} className="columns-1 lg:columns-2 2xl:columns-3 gap-3 [&>*]:mb-3 [&>*]:break-inside-avoid"
            pinClassName="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3 items-start">
            <Panel title="TFR D Cross" icon={Activity} accent="violet" testid="panel-poc" hint="price crossing prev-day TFR Core (TFR D) · bullish needs TFR W below, bearish TFR W above · all F&O">
              <PocCrossPanel hot={d.hot} />
            </Panel>

            <Panel title="TFR + Range Matrix Breakout" icon={Activity} accent="violet" testid="panel-tfradr" hint="TFR D cross that also broke its Range Matrix band">
              <TfrAdrPanel hot={d.hot} />
            </Panel>

            <Panel title="Opening Bell Volume" icon={Radar} accent="amber" testid="panel-opening" hint={`9:15 candle vs its usual volume · ${d.open_session ? `session ${d.open_session.slice(8, 10)}/${d.open_session.slice(5, 7)}` : "stays all day"}`}>
              <OpeningSurge rows={d.opening_surge || []} navigate={navigate} hot={d.hot} />
            </Panel>

            <Panel title="TFR Prime" icon={Target} accent="emerald" testid="panel-aligned" hint="price beyond Day, Week & Month Flow Zones · ⌚ entry time">
              <FlowNote />
              <BullBear data={{ bull: flow.filter(d.aligned.bull), bear: flow.filter(d.aligned.bear) }} navigate={navigate} testid="aligned" showTime hot={d.hot} marks={flow.marks} />
            </Panel>

            <Panel title="Sector Advances / Declines" icon={BarChart2} accent="cyan" testid="panel-sectors"
              hint="green = advances · red = declines · sorted best → worst sector">
              <SectorBars sectors={d.sectors} />
            </Panel>

            <Panel title="Big Money Volume" icon={Activity} accent="amber" testid="panel-rvol" hint="● 2★ strong ● 3★ extreme · aligned names only">
              <BullBear data={d.rvol} navigate={navigate} testid="rvol" showStar showRvol limit={5} />
            </Panel>

            <Panel title="Gaps > 1% (aligned)" icon={ArrowUpRight} accent="rose" testid="panel-gaps" hint="open vs prev close">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-semibold mb-1.5 text-emerald-600"><ArrowUpRight className="h-3 w-3" /> Gap Up <span className="text-slate-400 font-mono">({d.gap_up.length})</span></div>
                  <MiniList rows={d.gap_up} navigate={navigate} testid="gapup" limit={5} showGap />
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-semibold mb-1.5 text-rose-600"><ArrowDownRight className="h-3 w-3" /> Gap Down <span className="text-slate-400 font-mono">({d.gap_down.length})</span></div>
                  <MiniList rows={d.gap_down} navigate={navigate} testid="gapdown" limit={5} showGap />
                </div>
              </div>
            </Panel>

            <Panel title="Live Open Interest" icon={Activity} accent="blue" testid="panel-oi" hint="near-month futures · OI change vs previous session">
              <OIPanel />
            </Panel>

            <ZonePanel boxId="panel-zones" zones={d.zones} narrowPct={d.narrow_pct} multiCoil={d.multi_coil} weekBasis={d.week_basis} navigate={navigate} flow={flow} />

            <Panel title="Top Movers" icon={TrendingUp} accent="blue" testid="panel-movers" hint="by change % today">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-semibold mb-1.5 text-emerald-600"><ArrowUpRight className="h-3 w-3" /> Gainers</div>
                  <MiniList rows={d.gainers} navigate={navigate} testid="gainers" />
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-semibold mb-1.5 text-rose-600"><ArrowDownRight className="h-3 w-3" /> Losers</div>
                  <MiniList rows={d.losers} navigate={navigate} testid="losers" />
                </div>
              </div>
            </Panel>
          </ArrangeGrid>

          <Panel title="Stocks in News" icon={Newspaper} accent="violet" testid="panel-news" hint="latest F&O headlines">
            <NewsPanel news={news} />
          </Panel>
        </>
      )}
    </div>
  );
}
